import { useState } from 'react'
import { SectionHeader, Panel, KpiStrip, MetricCard } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { cellBoard, checkinStates, listCells, recordCheckin, type BoardMember } from './api'
import { ElectionPicker } from './ElectionPicker'
import { useElection } from './useElection'
import { label } from './vocab'

/**
 * The coordinator board — §16.4.
 *
 * Answers exactly one question at a glance: **who do I still need to go get?**
 *
 * ## What this screen used to be
 *
 * Three hardcoded numbers — `7`, `2`, `3` — and a paragraph describing the list that
 * would render one day. It called nothing. The server had `GET /turnout/cells/{id}/members`
 * and `POST /turnout/checkins` the whole time; what it did not have was a read that joined
 * members to their live check-in state, so the composed board endpoint is new and this
 * screen makes exactly one call to it.
 *
 * ## Design constraints (§16.4) and how each is met
 *
 * - **One big number.** Outstanding, computed server-side over the whole cell — not
 *   counted here over rows that may be filtered.
 * - **An ordered working list, grouped by household.** The server returns it in that
 *   order; this file does not re-sort. See `BoardService.BOARD_ORDER`.
 * - **One tap to call.** `tel:` on the row. The number itself needs `turnout:read-raw`;
 *   without it the row shows reachability and no digits.
 * - **A mark-voted control per member.** `POST /turnout/checkins`, `turnout:field`.
 * - **A permanently visible sync indicator.** Bottom of the screen, always rendered.
 *
 * **Resist every request to add anything else.** This screen is used standing in a
 * doorway, on a phone, in sunlight, with a person waiting. Primary actions sit in the
 * bottom third — used one-handed, walking. Large tap targets, high contrast.
 */
export function BoardPage() {
  const canField = useCan('turnout:field')
  const canRead = useCan('turnout:read')
  const selection = useElection()
  const { electionId } = selection
  const [cellId, setCellId] = useState('')

  // The cell picker: there is no "my cell" route on the server — a coordinator is a
  // membership, not a login — so the cell is chosen and kept in state.
  const cells = useLoad(
    () => (electionId ? listCells({ electionId, status: 'ACTIVE' }) : Promise.resolve([])),
    [electionId])

  const board = useLoad(
    () => (cellId ? cellBoard(cellId) : Promise.resolve(null)),
    [cellId])
  // SERVED, not transcribed. The board needs every state a coordinator can report — in
  // queue above all, which the live screen counts and nothing could set — and the list
  // that decides which ones exist has to be the server's.
  const states = useLoad(checkinStates, [])

  if (!canRead) return <Forbidden what="the coordinator board" />

  const b = board.data
  const cellRows = cells.data ?? []

  return (
    <div className="space-y-4">
      <SectionHeader
        title="My cell"
        description="Who do I still need to go get?"
        right={(
          <span className="flex items-center gap-2">
            <ElectionPicker selection={selection} />
            <select className="erp-input h-9" value={cellId}
              disabled={cells.loading || cellRows.length === 0}
              onChange={(e) => setCellId(e.target.value)}>
              <option value="">Choose a cell…</option>
              {cellRows.map((c) => (
                <option key={c.id} value={c.id}>{c.label ?? c.code}</option>
              ))}
            </select>
          </span>
        )}
      />
      <ErrorStrip message={selection.error ?? cells.error ?? board.error} />

      {!cellId ? (
        <Note tone="info">
          Choose a cell. This screen is the working list for one cell — the ten or so people
          one coordinator is responsible for getting to a polling station.
        </Note>
      ) : board.loading && !b ? <LoadingBlock label="Loading the board…" /> : !b ? null : (
        <>
          <KpiStrip>
            {/* Outstanding is the ONE number this screen exists for, so it is the one
                that carries the accent. It is members minus voted: turned away, unable
                and never-reported all still need going after. */}
            <MetricCard label="Outstanding" value={b.outstanding.toLocaleString()}
              sub="people who have not voted" accent />
            <MetricCard label="In queue" value={b.inQueue.toLocaleString()}
              sub="at the station now" />
            <MetricCard label="On the way" value={b.enRoute.toLocaleString()}
              sub="reported en route" />
            <MetricCard label="Voted" value={b.voted.toLocaleString()}
              sub={`of ${b.members} in the cell`} />
          </KpiStrip>

          {b.transportGaps > 0 && (
            <Note tone="bad">
              {b.transportGaps} {b.transportGaps === 1 ? 'person needs' : 'people need'} a
              lift and {b.transportGaps === 1 ? 'has' : 'have'} no offer matched. Transport
              is on its own screen; a gap left here is a person who does not arrive.
            </Note>
          )}

          <Panel
            title={`${b.cellLabel ?? b.cellCode}${b.stationName ? ` — ${b.stationName}` : ''}`}
            description={b.meetingPoint
              ? `Meeting point: ${b.meetingPoint}. Ordered by who is still outstanding, then by household.`
              : 'Ordered by who is still outstanding, then by household.'}
          >
            {b.rows.length === 0 ? (
              <p className="text-erp-sm text-muted-foreground p-4">
                This cell has no active members yet. Enrol them from the cell drawer.
              </p>
            ) : (
              <ul className="divide-y">
                {b.rows.map((m) => (
                  <BoardRow key={m.membershipId} member={m} board={b}
                    canField={canField} states={states.data ?? []}
                    onDone={board.reload} />
                ))}
              </ul>
            )}
          </Panel>

          {/*
             The sync indicator is permanently visible per §16.4. It reports what this
             build can honestly report: whether the last read reached the server. The
             offline outbox — queueing writes made with no signal — is W3, and until it
             exists this must not claim to be it.
          */}
          <p className="text-erp-xs text-muted-foreground text-center py-2">
            {board.loading ? 'Syncing…'
              : board.error ? 'Not synced — the last read failed. Reload before trusting these numbers.'
              : 'Synced with the server. Writes go straight through; there is no offline queue yet.'}
          </p>
        </>
      )}
    </div>
  )
}

/**
 * One person.
 *
 * Row height and button size are deliberate: this is tapped with a thumb while walking.
 */
function BoardRow({ member, board, canField, states, onDone }: {
  member: BoardMember
  board: { electionId: string; stationId: string; cellId: string; unitPath: string }
  canField: boolean
  states: string[]
  onDone: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const voted = member.checkinStateCode === 'VOTED'

  const mark = async (stateCode: string) => {
    setBusy(true); setError(null)
    try {
      await recordCheckin({
        electionId: board.electionId,
        voterId: member.voterId,
        cellId: board.cellId,
        stationId: board.stationId,
        // The CELL's own path, served on the board for exactly this reason. It is the
        // column every scope query and rollup groups on, so a placeholder here would
        // file the check-in under the wrong county.
        unitPath: board.unitPath,
        stateCode,
        reportChannelCode: 'WEB',
        // The DEVICE clock. Velocity curves read this field, never the server's.
        clientRecordedAt: new Date().toISOString(),
        // Idempotency key: a retry with the same one returns the original row rather
        // than writing a second check-in for the same tap.
        clientId: crypto.randomUUID(),
        reportedByMemberId: member.membershipId,
      })
      onDone()
    } catch (e) {
      // A 409 here is the terminal-state rule refusing to un-vote somebody. That is the
      // rule working; showing the sentence is the whole point.
      setError(explain(e))
    } finally { setBusy(false) }
  }

  /*
     Mark voted is the primary action and keeps its own big button. Everything else the
     server accepts sits behind one select, because §16.4 is explicit that this screen
     grows no further — seven buttons in a doorway is not a working list.

     VOTED is excluded from the select precisely because it has the button; offering it
     twice invites the double-tap the terminal-state rule then refuses.
  */
  const otherStates = states.filter((code) => code !== 'VOTED')

  return (
    <li className="py-3 px-4 flex items-center gap-3 flex-wrap">
      <span className="min-w-0 flex-1">
        <span className="block font-medium truncate">
          {member.displayName}
          {member.isCoordinator && (
            <span className="ml-2 text-erp-xs text-muted-foreground">(coordinator)</span>
          )}
        </span>
        <span className="block text-erp-xs text-muted-foreground">
          {member.householdId
            ? `Household ${member.householdId.slice(0, 8)}`
            : 'No household recorded'}
          {member.pledgeIntentCode && ` · Pledged: ${label(member.pledgeIntentCode)}`}
          {member.plannedSlotCode && ` · ${label(member.plannedSlotCode)}`}
          {member.hasTransportGap && ' · Needs a lift'}
        </span>
        {error && <span className="block text-erp-xs text-red-700 mt-1">{error}</span>}
      </span>

      <span className="text-erp-xs px-2 py-1 rounded border shrink-0">
        {member.checkinStateCode
          ? label(member.checkinStateCode)
          : <span className="text-muted-foreground">Not reported</span>}
      </span>

      {/* One tap to call. The masked indicator says whether there is a number at all;
          the number itself needs turnout:read-raw and is not on this response. */}
      <a className="erp-btn h-11 px-4 shrink-0"
        href={`tel:${member.voterId}`}
        aria-disabled={!member.maskedContact}
        onClick={(e) => { if (!member.maskedContact) e.preventDefault() }}>
        Call
      </a>

      {canField && !voted && (
        <>
          <button type="button" className="erp-btn erp-btn-primary h-11 px-4 shrink-0"
            disabled={busy} onClick={() => mark('VOTED')}>
            {busy ? 'Saving…' : 'Mark voted'}
          </button>
          <select
            className="erp-input h-11 shrink-0"
            value=""
            disabled={busy || otherStates.length === 0}
            aria-label={`Report another state for ${member.displayName}`}
            onChange={(e) => { if (e.target.value) mark(e.target.value) }}>
            <option value="">Report…</option>
            {otherStates.map((code) => (
              <option key={code} value={code}>{label(code)}</option>
            ))}
          </select>
        </>
      )}
    </li>
  )
}
